// import node module libraries
import { useState } from "react";
import { Card, Form, Button, Row, Col } from "react-bootstrap";

const CoursesMedia = (props) => {
  const { previous, createCourse, handleChange, isLoading, isEdit = false } = props;
  
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    createCourse();
  };

  const handleImageChange = (e) => {
    handleChange(e);
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    handleChange(e);
    const file = e.target.files[0];
    if (file) {
      setVideoPreview(file.name);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Main Card */}
      <Card className="media-form-card mb-4">
        <Card.Header className="media-form-header">
          <div className="d-flex align-items-center">
            <div className="header-icon me-3">
              <i className="fe fe-image"></i>
            </div>
            <div>
              <h4 className="mb-1">Course Media</h4>
              <p className="text-muted mb-0 small">Add visual content to attract learners</p>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="media-form-body">
          <Row className="g-4">
            {/* Course Cover Image */}
            <Col lg={6}>
              <div className="upload-section">
                <div className="upload-header mb-3">
                  <h5 className="upload-title">
                    <i className="fe fe-image me-2 text-primary"></i>
                    Course Cover Image
                  </h5>
                  <p className="upload-subtitle">
                    This image will be displayed on your course card
                  </p>
                </div>

                <div className="upload-area">
                  <input
                    type="file"
                    id="introImage"
                    name="introImage"
                    accept="image/png, image/gif, image/jpeg"
                    onChange={handleImageChange}
                    className="file-input"
                  />
                  <label htmlFor="introImage" className="file-label">
                    {imagePreview ? (
                      <div className="preview-container">
                        <img src={imagePreview} alt="Preview" className="image-preview" />
                        <div className="preview-overlay">
                          <i className="fe fe-edit-2"></i>
                          <span>Change Image</span>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon mb-3">
                          <i className="fe fe-upload-cloud"></i>
                        </div>
                        <h6 className="mb-2">Click to upload or drag and drop</h6>
                        <p className="upload-specs">
                          PNG, JPG or JPEG (750x440px recommended)
                        </p>
                      </div>
                    )}
                  </label>
                </div>

                <div className="upload-guidelines mt-3">
                  <p className="guidelines-title">
                    <i className="fe fe-info me-2"></i>
                    Image Guidelines
                  </p>
                  <ul className="guidelines-list">
                    <li>Minimum size: 750x440 pixels</li>
                    <li>Aspect ratio: 16:9 preferred</li>
                    <li>No text overlays on image</li>
                    <li>Clear, professional quality</li>
                  </ul>
                </div>
              </div>
            </Col>

            {/* Course Intro Video */}
            <Col lg={6}>
              <div className="upload-section">
                <div className="upload-header mb-3">
                  <h5 className="upload-title">
                    <i className="fe fe-video me-2 text-success"></i>
                    Course Introduction Video
                  </h5>
                  <p className="upload-subtitle">
                    Optional: Add a video preview of your course
                  </p>
                </div>

                <div className="upload-area">
                  <input
                    type="file"
                    id="introVideo"
                    name="introVideo"
                    accept="video/*"
                    onChange={handleVideoChange}
                    className="file-input"
                  />
                  <label htmlFor="introVideo" className="file-label">
                    {videoPreview ? (
                      <div className="video-preview-container">
                        <div className="video-icon mb-3">
                          <i className="fe fe-film"></i>
                        </div>
                        <h6 className="mb-2">{videoPreview}</h6>
                        <p className="text-muted small">Video uploaded successfully</p>
                        <Button variant="link" className="change-video-btn">
                          <i className="fe fe-edit-2 me-2"></i>
                          Change Video
                        </Button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <div className="upload-icon mb-3">
                          <i className="fe fe-video"></i>
                        </div>
                        <h6 className="mb-2">Click to upload video</h6>
                        <p className="upload-specs">
                          MP4, AVI, MOV (max 500MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>

                <div className="upload-guidelines mt-3">
                  <p className="guidelines-title">
                    <i className="fe fe-info me-2"></i>
                    Video Guidelines
                  </p>
                  <ul className="guidelines-list">
                    <li>Duration: 1-3 minutes recommended</li>
                    <li>HD quality (1080p preferred)</li>
                    <li>Clear audio without background noise</li>
                    <li>Professional presentation</li>
                  </ul>
                </div>
              </div>
            </Col>
          </Row>

          {/* Pro Tips */}
          <div className="pro-tips mt-4">
            <div className="pro-tips-header">
              <i className="fe fe-award me-2"></i>
              <strong>Pro Tips for Better Engagement</strong>
            </div>
            <Row className="g-3 mt-2">
              <Col md={6}>
                <div className="tip-item">
                  <i className="fe fe-check-circle text-success me-2"></i>
                  <span>Use bright, eye-catching images that represent your course content</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="tip-item">
                  <i className="fe fe-check-circle text-success me-2"></i>
                  <span>Keep intro videos concise and engaging to hook potential students</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="tip-item">
                  <i className="fe fe-check-circle text-success me-2"></i>
                  <span>Ensure images are high-resolution for mobile viewing</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="tip-item">
                  <i className="fe fe-check-circle text-success me-2"></i>
                  <span>Speak clearly and enthusiastically in your introduction video</span>
                </div>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <div className="form-actions">
        <Button 
          variant="" 
          className="btn-action btn-back" 
          onClick={previous} 
          disabled={isLoading}
        >
          <i className="fe fe-arrow-left me-2"></i>
          Previous
        </Button>
        <Button 
          variant="" 
          className="btn-action btn-submit" 
          type="submit" 
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            <>
              {isEdit ? 'Update Course' : 'Create Course'}
              <i className="fe fe-check ms-2"></i>
            </>
          )}
        </Button>
      </div>

      <style jsx>{`
        /* Card Styling */
        :global(.media-form-card) {
          border: none;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
        }

        :global(.media-form-header) {
          background: linear-gradient(135deg, #1d9eb0 0%, #3fc1d3 100%);
          color: white;
          padding: 2rem;
          border: none;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        :global(.media-form-header) h4 {
          color: white;
          margin: 0;
          font-weight: 700;
        }

        :global(.media-form-body) {
          padding: 2rem;
        }

        /* Upload Section */
        .upload-section {
          height: 100%;
        }

        .upload-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .upload-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          margin: 0;
        }

        /* Upload Area */
        .upload-area {
          position: relative;
          margin-bottom: 1rem;
        }

        .file-input {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .file-label {
          display: block;
          cursor: pointer;
          margin: 0;
        }

        .upload-placeholder {
          border: 2px dashed #cbd5e1;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          background: #f8f9fa;
          transition: all 0.3s ease;
        }

        .upload-placeholder:hover {
          border-color: #F68521;
          background: rgba(246, 133, 33, 0.05);
        }

        .upload-icon {
          font-size: 3rem;
          color: #cbd5e1;
        }

        .upload-placeholder:hover .upload-icon {
          color: #F68521;
        }

        .upload-specs {
          color: #94a3b8;
          font-size: 0.8125rem;
          margin: 0;
        }

        /* Image Preview */
        .preview-container {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
        }

        .image-preview {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 12px;
        }

        .preview-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(246, 133, 33, 0.9);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          color: white;
          font-weight: 600;
        }

        .preview-overlay i {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .preview-container:hover .preview-overlay {
          opacity: 1;
        }

        /* Video Preview */
        .video-preview-container {
          border: 2px solid #70a553;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          background: rgba(112, 165, 83, 0.05);
        }

        .video-icon {
          font-size: 3rem;
          color: #70a553;
        }

        :global(.change-video-btn) {
          color: #1d9eb0;
          text-decoration: none;
          padding: 0.5rem 1rem;
          font-weight: 600;
        }

        :global(.change-video-btn:hover) {
          color: #F68521;
        }

        /* Guidelines */
        .upload-guidelines {
          background: #f8f9fa;
          border-left: 4px solid #F68521;
          border-radius: 8px;
          padding: 1rem;
        }

        .guidelines-title {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.75rem;
          font-size: 0.875rem;
        }

        .guidelines-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .guidelines-list li {
          color: #64748b;
          font-size: 0.8125rem;
          padding: 0.25rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .guidelines-list li::before {
          content: "•";
          position: absolute;
          left: 0.5rem;
          color: #F68521;
          font-weight: bold;
        }

        /* Pro Tips */
        .pro-tips {
          background: linear-gradient(135deg, rgba(246, 133, 33, 0.1) 0%, rgba(29, 158, 176, 0.1) 100%);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .pro-tips-header {
          color: #1e293b;
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .tip-item {
          display: flex;
          align-items: flex-start;
          color: #64748b;
          font-size: 0.875rem;
        }

        .tip-item i {
          margin-top: 2px;
          flex-shrink: 0;
        }

        /* Action Buttons */
        .form-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        :global(.btn-action) {
          padding: 0.875rem 2rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          border: none;
        }

        :global(.btn-back) {
          background: #f1f5f9;
          color: #64748b;
        }

        :global(.btn-back:hover) {
          background: #e2e8f0;
          color: #1e293b;
        }

        :global(.btn-submit) {
          background: linear-gradient(135deg, #70a553 0%, #8bc86f 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(112, 165, 83, 0.3);
        }

        :global(.btn-submit:hover) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(112, 165, 83, 0.4);
        }

        :global(.btn-submit:disabled) {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Responsive */
        @media (max-width: 991px) {
          :global(.media-form-header) {
            padding: 1.5rem;
          }

          :global(.media-form-body) {
            padding: 1.5rem;
          }

          .upload-placeholder {
            padding: 2rem 1rem;
          }
        }

        @media (max-width: 768px) {
          .form-actions {
            flex-direction: column-reverse;
          }

          :global(.btn-action) {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </Form>
  );
};

export default CoursesMedia;